import {
  type AndCondition,
  ColumnType,
  type Condition,
  ConditionType,
  type EventCondition,
  type FunnelCondition,
  type NumberOfCondition,
  type OrCondition,
  type PropertyCondition,
  type ReferencedPropertyCondition,
  type SegmentSetCondition,
  type JourneySetCondition,
  JsonOperator,
  StringOperator,
  AggregationOption,
  PerUserAggregationType,
  AudienceAggregationType,
  type ColumnReference,
  type SyntheticColumn,
  isSyntheticColumn,
  isRelatedColumn,
  isTransformedColumn,
  isTraitColumn,
  type TraitColumn,
  type FormulaTraitConfig,
  TraitType,
  type ListTraitConfig,
  type RawSqlTraitConfig,
  type TraitConfig,
  type TrackedAudienceMembershipTraitConfig,
  isJsonPathColumn,
  type JsonPathColumn,
  type VisualQueryFilter,
} from "./types";
import type { Queryable } from "zapatos/db";

export const TRAIT_PREVIEW_ALIAS = "trait_preview";

/**
 * Returns the TraitColumn if the given column is a RelatedColumn or TransformedColumn
 * wrapping a trait, otherwise returns null.
 */
export function getWrappedTraitColumn(
  column: string | ColumnReference | null | undefined,
): TraitColumn | null {
  if (
    column != null &&
    (isRelatedColumn(column) || isTransformedColumn(column)) &&
    isTraitColumn(column.column)
  ) {
    return column.column;
  }
  return null;
}

export type VisitableCondition = Condition | FunnelCondition;
type VisitorFunction<TCondition> = (
  condition: TCondition,
  path: VisitableCondition[],
) => void;

/**
 * Visitor functions type. Exposes one function per condition type, to be executed
 * for each condition of that type in a given condition tree.
 *
 * Note that for now, this doesn't include And and Or conditions.
 */
export type AudienceVisitors = {
  // TODO it's kind of dangerous that these are all optional since then a new condition type can be added without code being updated
  [ConditionType.Property]?: VisitorFunction<PropertyCondition>;
  [ConditionType.ReferenceProperty]?: VisitorFunction<ReferencedPropertyCondition>;
  [ConditionType.Event]?: VisitorFunction<EventCondition>;
  [ConditionType.NumberOf]?: VisitorFunction<NumberOfCondition>;
  [ConditionType.SegmentSet]?: VisitorFunction<SegmentSetCondition>;
  [ConditionType.JourneySet]?: VisitorFunction<JourneySetCondition>;
};

export type VisitConditionOptions = {
  visitSubconditions?: boolean;
};

/**
 * Runs a depth-first search of the condition tree, executing any visitor functions
 * along the way. The deepest conditions in the tree will be visited first.
 *
 * @param conditions Root conditions to explore.
 * @param visitors Visitor functions to be executed for each condition in the tree.
 * @param options Options for visiting conditions, such as whether to visit subconditions.
 * @param path Accumulated path conditions visited in the tree.
 */
export function visitConditionList(
  conditions: Condition[],
  visitors: AudienceVisitors,
  options?: VisitConditionOptions,
  path: VisitableCondition[] = [],
) {
  for (const condition of conditions) {
    if (!condition) {
      continue;
    }
    visitCondition(condition, visitors, options ?? {}, path);
  }
}

/**
 * Runs a depth-first search of the condition tree, executing any visitor functions
 * along the way. The deepest conditions in the tree will be visited first.
 *
 * @param condition Root of the condition tree.
 * @param visitors Visitor functions to be executed for each condition in the tree.
 * @param options Options for visiting conditions, such as whether to visit subconditions.
 * @param path Accumulated path conditions visited in the tree.
 */
function visitCondition(
  condition: Condition,
  visitors: AudienceVisitors,
  options: VisitConditionOptions,
  path: VisitableCondition[],
) {
  const nextPath = [...path, condition];
  switch (condition.type) {
    case ConditionType.And:
    case ConditionType.Or:
      visitConditionList(condition.conditions, visitors, options, nextPath);
      break;
    case ConditionType.Property:
      visitPropertyCondition(condition, visitors, options, path);
      if (condition.subconditions && options.visitSubconditions !== false) {
        // Note, we don't do the extra check on subcondition conditions types, as in translateCondition()
        // Thus, there could be extra subconditions visited that do not actually do anything for purposes of query generation.
        visitConditionList(
          condition.subconditions,
          visitors,
          options,
          nextPath,
        );
      }
      if (ConditionType.Property in visitors) {
        visitors[ConditionType.Property]?.(condition, path);
      }
      break;
    case ConditionType.ReferenceProperty:
      visitPropertyCondition(condition, visitors, options, path);
      if (ConditionType.ReferenceProperty in visitors) {
        visitors[ConditionType.ReferenceProperty]?.(condition, path);
      }
      break;
    case ConditionType.Event: {
      if (condition.subconditions && options.visitSubconditions !== false) {
        visitConditionList(
          condition.subconditions,
          visitors,
          options,
          nextPath,
        );
      }
      if (
        condition.funnelCondition &&
        condition.funnelCondition.subconditions &&
        options.visitSubconditions !== false
      ) {
        visitConditionList(
          condition.funnelCondition.subconditions,
          visitors,
          options,
          [...nextPath, condition.funnelCondition],
        );
      }
      if (ConditionType.Event in visitors) {
        visitors[ConditionType.Event]?.(condition, path);
      }
      break;
    }
    case ConditionType.NumberOf:
      if (condition.subconditions && options.visitSubconditions !== false) {
        visitConditionList(
          condition.subconditions,
          visitors,
          options,
          nextPath,
        );
      }
      if (ConditionType.NumberOf in visitors) {
        visitors[ConditionType.NumberOf]?.(condition, path);
      }
      break;
    case ConditionType.SegmentSet:
      if (ConditionType.SegmentSet in visitors) {
        visitors[ConditionType.SegmentSet]?.(condition, path);
      }
      break;
    case ConditionType.JourneySet:
      if (ConditionType.JourneySet in visitors) {
        visitors[ConditionType.JourneySet]?.(condition, path);
      }
      break;
    default:
      throw new Error(
        `Unknown condition: ${JSON.stringify(condition satisfies never)}`,
      );
  }
}

function visitPropertyCondition(
  condition: PropertyCondition | ReferencedPropertyCondition,
  visitors: AudienceVisitors,
  options: VisitConditionOptions,
  path: VisitableCondition[],
) {
  const lhsTrait = getWrappedTraitColumn(condition.property);
  if (lhsTrait) {
    visitConditionList(lhsTrait.conditions ?? [], visitors, options, [
      ...path,
      condition,
    ]);
  }

  // ReferencedPropertyCondition omits `value`, so only PropertyCondition has it.
  if (condition.type === ConditionType.Property) {
    const rhsTrait = getWrappedTraitColumn(condition.value?.property);
    if (rhsTrait) {
      visitConditionList(rhsTrait.conditions ?? [], visitors, options, [
        ...path,
        condition,
      ]);
    }
  }
}

/**
 * Shorthand for creating an and condition.
 */
export function and<T extends Condition = Condition>(
  ...conditions: T[]
): AndCondition<T> {
  return {
    type: ConditionType.And,
    conditions: conditions,
  };
}

/**
 * Shorthand for creating an or condition.
 */
export function or<T extends Condition = Condition>(
  ...conditions: T[]
): OrCondition<T> {
  return {
    type: ConditionType.Or,
    conditions: conditions,
  };
}

export const numberEqualsCondition = (
  property: string,
  value: number,
): PropertyCondition => ({
  type: ConditionType.Property,
  property,
  propertyType: ColumnType.Number,
  operator: StringOperator.Equals,
  value,
});

// Given a user-facing AggregationOption, return the corresponding PerUserAggregationType and
// AudienceAggregationType.
export function getAggregationConfiguration(
  aggregation: AggregationOption | undefined,
  cumulative?: boolean,
): {
  aggregation: PerUserAggregationType;
  audienceAggregation: AudienceAggregationType;
} | null {
  if (!aggregation) {
    return null;
  }

  switch (aggregation) {
    case AggregationOption.Count: {
      if (cumulative) {
        return {
          aggregation: PerUserAggregationType.Count,
          audienceAggregation: AudienceAggregationType.Cumulative,
        };
      } else {
        return {
          aggregation: PerUserAggregationType.Count,
          audienceAggregation: AudienceAggregationType.Sum,
        };
      }
    }
    case AggregationOption.CountDistinctProperty:
      return {
        aggregation: PerUserAggregationType.CountDistinct,
        audienceAggregation: AudienceAggregationType.None,
      };
    case AggregationOption.UniqueUsers: {
      if (cumulative) {
        return {
          aggregation: PerUserAggregationType.UniqueUsers,
          audienceAggregation: AudienceAggregationType.Cumulative,
        };
      } else {
        return {
          aggregation: PerUserAggregationType.UniqueUsers,
          audienceAggregation: AudienceAggregationType.Sum,
        };
      }
    }
    case AggregationOption.PercentOfAudience:
      return {
        aggregation: PerUserAggregationType.UniqueUsers,
        audienceAggregation: AudienceAggregationType.PercentOfAudience,
      };
    case AggregationOption.AverageOfProperty:
      return {
        aggregation: PerUserAggregationType.Average,
        audienceAggregation: AudienceAggregationType.None,
      };
    case AggregationOption.AverageOfPropertyPerUser:
      return {
        aggregation: PerUserAggregationType.Average,
        audienceAggregation: AudienceAggregationType.Average,
      };
    case AggregationOption.SumOfProperty:
      return {
        aggregation: PerUserAggregationType.Sum,
        audienceAggregation: cumulative
          ? AudienceAggregationType.Cumulative
          : AudienceAggregationType.Sum,
      };
    case AggregationOption.SumOfPropertyPerUser:
      return {
        aggregation: PerUserAggregationType.Sum,
        audienceAggregation: AudienceAggregationType.Average,
      };
    default:
      throw new Error(`Unknown aggregation option: ${aggregation}`);
  }
}

/**
 * Maps a (PerUserAggregationType, AudienceAggregationType) pair to an AggregationOption.
 * Used for translating backend aggregation config to a user-facing option.
 */
export function mapAggregationConfigurationToConfigurationOption({
  aggregation,
  audienceAggregation,
}: {
  aggregation: PerUserAggregationType;
  audienceAggregation: AudienceAggregationType;
}): AggregationOption | null {
  switch (aggregation) {
    case PerUserAggregationType.Count:
      if (audienceAggregation === AudienceAggregationType.Sum) {
        return AggregationOption.Count;
      }
      break;
    case PerUserAggregationType.CountDistinct:
      if (audienceAggregation === AudienceAggregationType.None) {
        return AggregationOption.CountDistinctProperty;
      }
      break;
    case PerUserAggregationType.Sum:
      if (
        audienceAggregation === AudienceAggregationType.Sum ||
        audienceAggregation === AudienceAggregationType.Cumulative
      ) {
        return AggregationOption.SumOfProperty;
      } else if (audienceAggregation === AudienceAggregationType.Average) {
        return AggregationOption.SumOfPropertyPerUser;
      }
      break;
    case PerUserAggregationType.UniqueUsers:
      if (
        audienceAggregation === AudienceAggregationType.Sum ||
        audienceAggregation === AudienceAggregationType.Cumulative
      ) {
        return AggregationOption.UniqueUsers;
      } else if (
        audienceAggregation === AudienceAggregationType.PercentOfAudience
      ) {
        return AggregationOption.PercentOfAudience;
      }
      break;
    case PerUserAggregationType.Average:
      if (audienceAggregation === AudienceAggregationType.None) {
        return AggregationOption.AverageOfProperty;
      }
      if (audienceAggregation === AudienceAggregationType.Average) {
        return AggregationOption.AverageOfPropertyPerUser;
      }
      break;
    default:
      return AggregationOption.Count;
  }
  // Should never reach here
  return null;
}

export function getColumnName(
  column: ColumnReference | SyntheticColumn | null | undefined,
) {
  if (!column) {
    return null;
  }
  if (column.type === "raw" || isSyntheticColumn(column)) {
    return column.name;
  } else if (column.type === "related") {
    return getColumnName(column.column);
  } else if (column.type === "trait") {
    return null;
  }

  return null;
}

export function getTraitColumnType(type: TraitType, config: TraitConfig) {
  switch (type) {
    case TraitType.Count:
    case TraitType.Sum:
    case TraitType.Average:
      return ColumnType.Number;
    case TraitType.First:
    case TraitType.Last:
    case TraitType.MostFrequent:
    case TraitType.LeastFrequent:
      // TODO: Get the column type from the relationship
      return ColumnType.Unknown;
    case TraitType.RawSql:
      return (config as RawSqlTraitConfig).resultingType;
    case TraitType.Formula:
      return (config as FormulaTraitConfig).resultingType;
    case TraitType.List:
      return (config as ListTraitConfig).resultingType;
    case TraitType.TrackedAudienceMembership:
      // JsonArrayStrings for "id" output, Json for "object" output — derived
      // from the list's output_format and stored on the trait config.
      return (config as TrackedAudienceMembershipTraitConfig).resultingType;
    case TraitType.Predictive:
      return ColumnType.Number;
    default:
      return ColumnType.Unknown;
  }
}

// Alias for internal split UUID in audience_splits table, exposed to customer
export const SPLIT_ID_ALIAS = "ht_split_id";

// Get the final (casted) propertyType of a property condition
// This is only applicable to Json propertyTypes that have a JsonPath and castType
export function finalPropertyType(condition: PropertyCondition) {
  if (condition.propertyType != ColumnType.Json) {
    return condition.propertyType;
  }
  // When the operator is a non-Json operator, we know this is not a typical
  // Json condition, and it's a jsonPath condition. return the cast type
  if (!Object.values(JsonOperator).includes(condition.operator as any)) {
    return (condition.property as JsonPathColumn).castType;
  }
  return condition.propertyType;
}

export function containsJsonPathProperty(
  subcondition:
    | AndCondition<PropertyCondition>
    | OrCondition<PropertyCondition>,
): boolean {
  return subcondition.conditions.some(
    (c) =>
      c.type === ConditionType.Property &&
      c.property &&
      isJsonPathColumn(c.property),
  );
}

/**
 * Infers a primary key for a parent model when previewing a trait on a related model.
 *
 * When previewing a trait that belongs to a related model, the parent model usually won't have
 * a primary key configured (event models may have one). In this case, we can use the relationship's
 * from_column (the join key in the parent model) as a virtual primary key. In the case of a Formula trait
 * we use the first referenced column. Keep in mind these are not true PKs, but just used for labeling purpose.
 *
 * @param pgClient - Database client for querying
 * @param additionalColumns - The additional columns from the visual query filter
 * @returns The from_column to use as a virtual primary key, or null if not found
 */
export async function inferPrimaryKeyForRelatedModel(
  pgClient: Queryable,
  additionalColumns?: VisualQueryFilter["additionalColumns"],
): Promise<string | null> {
  if (!additionalColumns) {
    return null;
  }

  // Look for a trait preview column (added by usePreviewTrait hook)
  const traitPreviewColumn = additionalColumns.find(
    (col) => col.alias === TRAIT_PREVIEW_ALIAS,
  );

  if (!traitPreviewColumn) {
    return null;
  }

  let relationshipId: string | null = null;

  const columnType = traitPreviewColumn.column.type;
  switch (columnType) {
    // Handle regular related columns (non-formula traits)
    case "related": {
      const relationshipPath = traitPreviewColumn.column.path;
      if (!relationshipPath || relationshipPath.length === 0) {
        return null;
      }
      relationshipId = relationshipPath[0] ?? null;
      break;
    }
    // Handle formula traits on related models
    case "transformed": {
      const innerColumn = traitPreviewColumn.column.column;
      // Check if this is a formula trait
      if (
        innerColumn.type === "inline_trait" &&
        innerColumn.traitType === TraitType.Formula
      ) {
        // Just use the first rawColumn as an inferred PK
        for (const refCol of innerColumn.traitConfig.referencedColumns) {
          if (refCol.column.type === "raw") {
            // Return the column name directly as the primary key
            return refCol.column.name;
          }
        }
      }
      break;
    }
    case "static_value":
      break;
    default:
      throw new Error(`Unknown column type: ${columnType satisfies never}`);
  }

  if (!relationshipId) {
    return null;
  }

  // Query the direct_model_relationships table to get the from_column
  const { rows } = await pgClient.query(
    `SELECT from_column
     FROM direct_model_relationships
     WHERE relationship_id = $1
     LIMIT 1`,
    [relationshipId],
  );

  if (rows.length > 0 && rows[0].from_column) {
    return rows[0].from_column;
  }

  return null;
}

/**
 * Given a parent and child model, returns the join key column on the child
 * model side. This is the `to_column` from `direct_model_relationships` where
 * the child is the `to_model_id`.
 *
 * Useful for warehouse distribution hints (e.g. Synapse HASH distribution)
 * where the child model's output columns don't include the parent's primary
 * key but do include the join key.
 */
export async function getJoinKeyForRelatedModel(
  pgClient: Queryable,
  parentModelId: string,
  childModelId: string,
): Promise<string | null> {
  const { rows } = await pgClient.query(
    `SELECT dr.to_column
     FROM direct_model_relationships dr
     WHERE dr.from_model_id = $1 AND dr.to_model_id = $2
     LIMIT 1`,
    [parentModelId, childModelId],
  );

  if (rows.length === 0) {
    return null;
  }

  return rows[0].to_column;
}
